import type { IconName } from "@similarweb/icons";

import type { ICampaignLocationAndLanguage } from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import type { IEditableSearchEngine } from "app/components/campaigns/searchEngine/SearchEnginesConsts";
import { EditMode, ESearchEngineDevice } from "app/components/campaigns/searchEngine/SearchEnginesConsts";
import { getSearchEngineLangAsText } from "app/components/campaigns/searchEngine/SearchEngineUtils";
import type { ICampaignsScrapingConfiguration } from "app/components/campaigns/shared/CampaignsConst";
import type { ISearchEnginesKeywordsProps } from "app/components/React/Table/cells/SearchEnginesKeywordCell/SearchEnginesKeywordCellConst";
import { translate } from "app/filters/i18nFilter";
import { handleTargetUrlButtonIconAndColor } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsCampaignUtils";
import type { IKeywordAndTags } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import {
    normalizeKeywordMap,
    normalizeScrapingConfigurations,
} from "app/pages/digital-marketing/keywords-campaigns/shared/utils/keywordNormalization";
import type { ISupportedLanguagesAndLocale } from "app/pages/digital-marketing/keywords-campaigns/wizard/campaigns-wizard/types";
import type { LOCATION_SORT_ORDER } from "app/pages/digital-marketing/keywords-campaigns/wizard/constants";

export const getTooltipTextForTargetUrlIcon = (buttonIcon: IconName): string => {
    switch (buttonIcon) {
        case "Install":
            return translate("rankTracker.keywordsCampaigns.targetUrl.set.button.tooltip");
        case "alert-circle":
            return translate("rankTracker.keywordsCampaigns.targetUrl.mismatch.button.tooltip");
        case "checked-circle":
            return translate("rankTracker.keywordsCampaigns.targetUrl.match.button.tooltip");
        default:
            return translate("rankTracker.keywordsCampaigns.targetUrl.click.button.tooltip");
    }
};

export const getAllKeywordsOfTableFronmAllScrapingConfigurations = (
    scrapingConfigurations: ICampaignsScrapingConfiguration[],
): string[] => {
    const allKeywords = new Set<string>();
    scrapingConfigurations.forEach((item) => {
        item.keywords.forEach((keyword) => {
            allKeywords.add(keyword);
        });
    });
    return Array.from(allKeywords);
};

export const getSearchEngineValues = (
    config: ICampaignsScrapingConfiguration[],
    keyword: string,
    languageList: ISupportedLanguagesAndLocale[],
): ISearchEnginesKeywordsProps[] => {
    return config?.map((scrapingConfigItem) => {
        const keywordsSet = new Set(scrapingConfigItem.keywords);
        const doesKeywordExistInSearchEngine = keywordsSet.has(keyword);
        const valueOfSearchEngine = [
            scrapingConfigItem.geoLocation,
            scrapingConfigItem.locale,
            scrapingConfigItem.device,
        ].join("_");
        const searchEngineLanguageAsText = getSearchEngineLangAsText(languageList, scrapingConfigItem.locale);
        const availableLocationsObject = {
            countryIconID: scrapingConfigItem.geoLocationCountry,
            id: valueOfSearchEngine,
            text: `${scrapingConfigItem.geoLocation} (${searchEngineLanguageAsText})`,
            device: scrapingConfigItem.device,
            isSelected: !!doesKeywordExistInSearchEngine,
        };
        return availableLocationsObject;
    });
};

export const convertKeywordsAndTagsFromObjectToTableFormat = (
    scrapingConfigurations: ICampaignsScrapingConfiguration[],
    tags: Record<string, string[]>,
    keywordToTargetUrls: Record<string, string[]>,
    languageList: ISupportedLanguagesAndLocale[],
): IKeywordAndTags[] => {
    if (!scrapingConfigurations) {
        return [];
    }
    // Normalize upfront so whitespace-variant duplicates are merged into single rows
    const normalizedConfs = normalizeScrapingConfigurations(scrapingConfigurations);
    const normalizedTags = normalizeKeywordMap(tags);
    const normalizedTargetUrls = normalizeKeywordMap(keywordToTargetUrls);

    const allKeywordsFromScrapingConfig = getAllKeywordsOfTableFronmAllScrapingConfigurations(normalizedConfs);
    const scrapingConfsToIterate = normalizedConfs.map((sc) => ({ ...sc }));

    return allKeywordsFromScrapingConfig.reduce((convertedKeywordsAndTags: IKeywordAndTags[], keyword) => {
        const keywordSearchEngine = getSearchEngineValues(scrapingConfsToIterate, keyword, languageList);
        const keywordTargetUrl = normalizedTargetUrls?.[keyword]?.map((targetUrl) => targetUrl) ?? [];
        const keywordAndTags: IKeywordAndTags = {
            keyword,
            tags: normalizedTags?.[keyword]?.map((tag) => ({ text: tag })) ?? [],
            searchEngine: keywordSearchEngine,
            keywordToTargetUrls: keywordTargetUrl,
            targetUrlIcon: handleTargetUrlButtonIconAndColor(keywordTargetUrl),
        };

        convertedKeywordsAndTags.push(keywordAndTags);
        return convertedKeywordsAndTags;
    }, []);
};

const LANGUAGE_DESCRIPTION_SEPARATOR = " - ";

export const getSearchEnginesOfCurrentCampaign = (
    locationsAndLanguagesLists: ICampaignLocationAndLanguage,
    scrapingConfigurations: ICampaignsScrapingConfiguration[],
): IEditableSearchEngine[] => {
    return scrapingConfigurations?.map((ScrapeConf: ICampaignsScrapingConfiguration) => {
        const languageDetails = locationsAndLanguagesLists.languagesList?.find((langAndLocale) => {
            return langAndLocale?.locale === ScrapeConf?.locale;
        });
        const iconName = ScrapeConf.searchEngine.toLowerCase();
        const definedSearchEngine = ScrapeConf.searchEngine;
        const definedGeoLocation = ScrapeConf.geoLocation;
        const languageText = languageDetails?.description.includes(LANGUAGE_DESCRIPTION_SEPARATOR)
            ? languageDetails?.description.split(LANGUAGE_DESCRIPTION_SEPARATOR)[1]
            : languageDetails?.description;
        return {
            editMode: EditMode.Saved,
            searchEngineDetails: {
                company: {
                    id: definedSearchEngine,
                    text: definedSearchEngine,
                    iconName: iconName as IconName,
                },
                device: ESearchEngineDevice[ScrapeConf.device.toUpperCase()],
                id: ScrapeConf.id || "",
                location: {
                    id: ScrapeConf.geoLocationCountry,
                    text: definedGeoLocation,
                    type: ScrapeConf.geoLocationType as (typeof LOCATION_SORT_ORDER)[number],
                },
                language: {
                    id: languageDetails?.locale || "",
                    text: languageText || "",
                },
            },
        };
    });
};
